import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Input,
  Divider,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import PlumbingIcon from '@mui/icons-material/Plumbing';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
    padding: 0,
  },
  box: {
    width: '100%',
  },
  content: {
    marginLeft: '0px',
    padding: '80px 30px',
  },
}));

interface SubmissionFormData {
  submissionCategory: string;
  customerName: string;
  address: string;
  testDate: string;
  orderNumber: string;
  waterAuthority: string;
  technicianName: string;
  // Electronic Submission fields
  portalUsername: string;
  portalPassword: string;
  confirmationNumber: string;
  // Email Submission fields
  recipientEmail: string;
  ccEmails: string;
  emailSubject: string;
  attachments: File[];
  // Manual Submission fields
  mailingAddress: string;
  contactPerson: string;
  submissionDeadline: string;
}

const submissionCategories = [
  { value: 'electronic', label: 'Electronic Submission' },
  { value: 'email', label: 'Email Submission' },
  { value: 'manual', label: 'Manual Submission' },
];

const WaterAuthoritySubmissionForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { submissionId } = useParams<{ submissionId: string }>();

  const [formData, setFormData] = useState<SubmissionFormData>({
    submissionCategory: '',
    customerName: '',
    address: '',
    testDate: '',
    orderNumber: '',
    waterAuthority: '',
    technicianName: '',
    portalUsername: '',
    portalPassword: '',
    confirmationNumber: '',
    recipientEmail: '',
    ccEmails: '',
    emailSubject: '',
    attachments: [],
    mailingAddress: '',
    contactPerson: '',
    submissionDeadline: '',
  });

  useEffect(() => {
    // Mock data based on submission ID - in real app this would fetch from API
    if (submissionId) {
      const mockSubmissionData = {
        submissionCategory: 'electronic',
        customerName: 'Pierogies Plus',
        address: '342 Island Ave.',
        testDate: '2025-09-04',
        orderNumber: 'B12345',
        waterAuthority: 'PWSA',
        technicianName: 'John Smith',
        portalUsername: '',
        portalPassword: '',
        confirmationNumber: '',
        recipientEmail: 'backflow@pgh2o.com',
        ccEmails: '',
        emailSubject: 'Backflow Test Submission - B12345',
        attachments: [],
        mailingAddress: '',
        contactPerson: '',
        submissionDeadline: '2025-09-11',
      };
      setFormData(mockSubmissionData);
    }
  }, [submissionId]);

  const handleInputChange = (field: keyof SubmissionFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (files: FileList | null) => {
    if (files) {
      setFormData((prev) => ({
        ...prev,
        attachments: Array.from(files),
      }));
    }
  };

  const handleSubmit = () => {
    console.log('Submitting water authority form:', formData);
    // Handle form submission logic here
    alert('Water Authority submission completed!');
  };

  const renderCategorySpecificFields = () => {
    switch (formData.submissionCategory) {
      case 'electronic':
        return (
          <>
            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Electronic Portal Information
            </Typography>
            <TextField
              label="Portal Username"
              value={formData.portalUsername}
              onChange={(e) => handleInputChange('portalUsername', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
            <TextField
              label="Portal Password"
              type="password"
              value={formData.portalPassword}
              onChange={(e) => handleInputChange('portalPassword', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
            <TextField
              label="Confirmation Number"
              value={formData.confirmationNumber}
              onChange={(e) => handleInputChange('confirmationNumber', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
              helperText="Enter confirmation number after successful submission"
            />
          </>
        );

      case 'email':
        return (
          <>
            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Email Submission Details
            </Typography>
            <TextField
              label="Recipient Email"
              type="email"
              value={formData.recipientEmail}
              onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
            <TextField
              label="CC Emails (comma separated)"
              value={formData.ccEmails}
              onChange={(e) => handleInputChange('ccEmails', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
            <TextField
              label="Email Subject"
              value={formData.emailSubject}
              onChange={(e) => handleInputChange('emailSubject', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Attachments (Test Reports, Certificates)
              </Typography>
              <Input
                type="file"
                inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.png', multiple: true }}
                onChange={(e) => handleFileChange((e.target as HTMLInputElement).files)}
                fullWidth
              />
            </Box>
          </>
        );

      case 'manual':
        return (
          <>
            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
              Manual Submission Details
            </Typography>
            <TextField
              label="Mailing Address"
              multiline
              rows={3}
              value={formData.mailingAddress}
              onChange={(e) => handleInputChange('mailingAddress', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
            <TextField
              label="Contact Person"
              value={formData.contactPerson}
              onChange={(e) => handleInputChange('contactPerson', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
            <TextField
              label="Submission Deadline"
              type="date"
              value={formData.submissionDeadline}
              onChange={(e) => handleInputChange('submissionDeadline', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 3 }}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.box}>
        <Box className={classes.content}>
          <PageHeader
            titleIcon={<PlumbingIcon />}
            mainTitle="Backflow Testing"
            subTitle="Water Authority Submission Form"
            backButtonLabel="Submissions"
            backButtonLink="/backflow_tests/water_authority_submissions"
          />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Submission Information
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Submission Category</InputLabel>
                        <Select
                          value={formData.submissionCategory}
                          label="Submission Category"
                          onChange={(e) => handleInputChange('submissionCategory', e.target.value)}
                        >
                          {submissionCategories?.map((category) => (
                            <MenuItem key={category.value} value={category.value}>
                              {category.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Water Authority"
                        value={formData.waterAuthority}
                        onChange={(e) => handleInputChange('waterAuthority', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{ mb: 3 }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" component="h3" gutterBottom>
                    Job Details
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Customer Name"
                        value={formData.customerName}
                        onChange={(e) => handleInputChange('customerName', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{ mb: 3 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Order Number"
                        value={formData.orderNumber}
                        onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{ mb: 3 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{ mb: 3 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Test Date"
                        type="date"
                        value={formData.testDate}
                        onChange={(e) => handleInputChange('testDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{ mb: 3 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Technician Name"
                        value={formData.technicianName}
                        onChange={(e) => handleInputChange('technicianName', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{ mb: 3 }}
                      />
                    </Grid>
                  </Grid>

                  {formData.submissionCategory && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      {renderCategorySpecificFields()}
                    </>
                  )}

                  <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={!formData.submissionCategory}
                    >
                      {formData.submissionCategory === 'manual'
                        ? 'Download Report for Submission'
                        : 'Submit to Water Authority'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => history.push('/backflow_tests/water_authority_submissions')}
                    >
                      Cancel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default WaterAuthoritySubmissionForm;
